/**
 * Currency Utility Functions
 * Handles currency symbol conversion and price formatting
 */

/**
 * The one symbol list for the whole app. A currency gets a symbol only when
 * the symbol is unambiguous: ¥ is deliberately absent because it is both CNY
 * and JPY, and a procurement decision should not rest on guessing which.
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    INR: '₹',
    AUD: 'A$',
    CAD: 'C$',
};

/**
 * Get currency symbol for a given currency code. A code without a symbol is
 * returned as-is with a non-breaking space, so "SGD 420" reads as a unit and
 * never wraps between code and amount.
 */
export const getCurrencySymbol = (currencyCode: string = 'INR'): string => {
    const code = currencyCode.trim().toUpperCase();
    return CURRENCY_SYMBOLS[code] || `${code}\u00A0`;
};

/**
 * Format a price with currency symbol
 */
export const formatPrice = (
    price: number,
    currency: string = 'INR',
    locale: string = 'en-IN'
): string => {
    const symbol = getCurrencySymbol(currency);
    const formattedNumber = price.toLocaleString(locale);
    return `${symbol}${formattedNumber}`;
};

/**
 * Format a price range with currency symbol
 */
export const formatPriceRange = (
    minPrice: number,
    maxPrice: number,
    currency: string = 'INR',
    locale: string = 'en-IN'
): string => {
    const symbol = getCurrencySymbol(currency);
    const minFormatted = minPrice.toLocaleString(locale);
    const maxFormatted = maxPrice.toLocaleString(locale);
    return `${symbol}${minFormatted} - ${symbol}${maxFormatted}`;
};

/**
 * Parse currency code to determine appropriate locale
 */
export const getCurrencyLocale = (currencyCode: string = 'INR'): string => {
    const localeMap: Record<string, string> = {
        USD: 'en-US',
        EUR: 'de-DE',
        GBP: 'en-GB',
        INR: 'en-IN',
        JPY: 'ja-JP',
        CNY: 'zh-CN',
        AUD: 'en-AU',
        CAD: 'en-CA',
    };

    return localeMap[currencyCode.toUpperCase()] || 'en-US';
};
